import type { CharacterData } from '~/types/storage';
import type { Skill } from '~/pages/CharacterSheet/types';
import type { RollItem } from '~/utils/rollUtils';
import { getSkillChecks } from '~/utils/glossaryStats';
import { evaluateFormula } from '~/utils/formulaEvaluator';
import { getAllRollableItems } from '~/utils/rollUtils';
import { GLOSSARY } from '~/generated/glossary.data';

const SKILL_CHECK_ENTRIES = getSkillChecks();

/**
 * Computed values derived from CharacterData
 */
export interface DerivedCharacterValues {
  skills: Skill[];
  movement: number;
  hpMax: number;
  resilienceMax: number;
  rollableItems: RollItem[];
}

/**
 * Computes movement from core stats using glossary formula
 */
export function computeMovement(coreStats: Record<string, number>): number {
  const movementEntry = GLOSSARY['Movement Speed'];
  const formula = movementEntry.metadata.formula;
  return evaluateFormula(formula, coreStats);
}

/**
 * Computes maximum HP from core stats using glossary formula
 */
export function computeHpMax(coreStats: Record<string, number>): number {
  const hpEntry = GLOSSARY['Health Points'];
  const formula = hpEntry.metadata.formula;
  return evaluateFormula(formula, coreStats);
}

/**
 * Computes maximum Resilience from core stats using glossary formula
 */
export function computeResilienceMax(coreStats: Record<string, number>): number {
  const resilienceEntry = GLOSSARY['Resilience'];
  const formula = resilienceEntry.metadata.formula;
  return evaluateFormula(formula, coreStats);
}

/**
 * Computes all skills from glossary entries and core stats
 */
export function computeSkills(coreStats: Record<string, number>): Skill[] {
  return SKILL_CHECK_ENTRIES.map(entry => ({
    name: entry.id,
    glossaryId: entry.id,
    formula: entry.metadata.formula || '',
    pool: evaluateFormula(entry.metadata.formula || '0', coreStats),
  }));
}

/**
 * Computes all derived character values in one pass
 */
export function computeAllDerived(characterData: CharacterData): DerivedCharacterValues {
  const { coreStats } = characterData;
  const skills = computeSkills(coreStats);
  const movement = computeMovement(coreStats);

  return {
    skills,
    movement,
    hpMax: computeHpMax(coreStats),
    resilienceMax: computeResilienceMax(coreStats),
    rollableItems: getAllRollableItems(coreStats, movement, skills),
  };
}
